/**
 * Formulario de envío de recetas
 *
 * Recoge los datos de contacto, el nombre y el texto de la receta, las fotos
 * obligatorias y los dos consentimientos (publicación y política de
 * privacidad), y los envía mediante el servicio `recipeSubmission`. Sigue el
 * mismo lenguaje visual y patrón de estado que `ContactForm`
 * (frontend/src/components/contact/ContactForm.jsx): objeto `errors`,
 * `isSubmitting`, `isSubmitted` y bloque de confirmación en verde.
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  submitRecipe,
  MAX_IMAGENES,
  MIN_IMAGENES,
  MAX_TAMANO_IMAGEN_BYTES,
  TIPOS_IMAGEN_PERMITIDOS,
} from '../../services/recipeSubmission';

/** Longitud mínima exigida para el texto de la receta. */
const MIN_CARACTERES_RECETA = 30;

/** Longitud mínima exigida para el nombre de la receta. */
const MIN_CARACTERES_NOMBRE_RECETA = 3;

/** Longitud máxima exigida para el nombre de la receta. */
const MAX_CARACTERES_NOMBRE_RECETA = 120;

/** Texto exacto del checkbox de consentimiento de publicación (obligatorio). */
const TEXTO_CONSENTIMIENTO =
  'Autorizo a Intercasa a publicar mi receta y las imágenes enviadas en su web y redes sociales, sin mostrar mis datos personales.';

/**
 * Formatea el tamaño máximo de imagen en MB para mostrarlo en mensajes de error.
 * @returns {string} Tamaño máximo en MB, p. ej. "5".
 */
const tamanoMaximoEnMB = () => (MAX_TAMANO_IMAGEN_BYTES / (1024 * 1024)).toString();

/**
 * Formulario de envío de recetas de usuarios.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.onEnviado - Se invoca cuando el envío ha terminado
 *   con éxito y el modal contenedor debe cerrarse.
 * @param {Function} [props.onEstadoEnvio] - Avisa al contenedor de si el
 *   formulario está mostrando la pantalla de confirmación, para que pueda
 *   adaptar su propia interfaz (el modal esconde su cabecera).
 * @returns {JSX.Element} Formulario de envío de receta.
 */
const RecipeSubmissionForm = ({ onEnviado, onEstadoEnvio }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    nombre_receta: '',
    receta: '',
    consentimientoPublicacion: false,
    aceptaPrivacidad: false,
  });

  // Las imágenes se guardan como { file, previewUrl } para poder mostrar
  // miniaturas sin volver a leer el fichero en cada render.
  const [imagenes, setImagenes] = useState([]);
  const [errors, setErrors] = useState({});
  const [errorEnvio, setErrorEnvio] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Revoca las object URLs de las miniaturas al desmontar el formulario,
  // para no filtrar memoria del navegador. Usamos una ref con el valor más
  // reciente de `imagenes` en vez de depender directamente del array: si el
  // efecto dependiera de `imagenes`, su cleanup se ejecutaría en CADA
  // cambio de la lista (no solo al desmontar) y revocaría la URL de una
  // imagen que sigue mostrándose (p. ej. al añadir una segunda foto se
  // revocaría la miniatura de la primera). Quitar una imagen concreta ya
  // revoca su URL individualmente en `quitarImagen`.
  const imagenesRef = useRef(imagenes);
  imagenesRef.current = imagenes;

  useEffect(() => () => {
    imagenesRef.current.forEach((imagen) => URL.revokeObjectURL(imagen.previewUrl));
  }, []);

  // Tras un envío exitoso, cierra el modal pasado un tiempo prudencial para
  // que el usuario pueda leer el mensaje de confirmación.
  useEffect(() => {
    if (!isSubmitted) return undefined;

    const timeoutId = setTimeout(() => {
      onEnviado();
    }, 2500);

    return () => clearTimeout(timeoutId);
  }, [isSubmitted, onEnviado]);

  // Mantiene informado al modal contenedor del estado de confirmación.
  useEffect(() => {
    if (onEstadoEnvio) onEstadoEnvio(isSubmitted);
  }, [isSubmitted, onEstadoEnvio]);

  /**
   * Maneja los cambios en los inputs de texto y en el checkbox de
   * consentimiento, y limpia el error de ese campo en cuanto el usuario
   * empieza a corregirlo.
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Valida los ficheros seleccionados (cantidad, tipo y tamaño) y añade a
   * la lista de imágenes solo los que superan la validación, generando su
   * URL de previsualización con `URL.createObjectURL`.
   */
  const handleImagenesChange = (e) => {
    const ficheros = Array.from(e.target.files || []);
    // Permite volver a seleccionar el mismo fichero tras quitarlo.
    e.target.value = '';

    if (ficheros.length === 0) return;

    if (imagenes.length + ficheros.length > MAX_IMAGENES) {
      setErrors((prev) => ({
        ...prev,
        imagenes: `Solo puedes adjuntar un máximo de ${MAX_IMAGENES} imágenes.`,
      }));
      return;
    }

    const ficherosInvalidos = ficheros.filter(
      (fichero) => !TIPOS_IMAGEN_PERMITIDOS.includes(fichero.type)
    );
    if (ficherosInvalidos.length > 0) {
      setErrors((prev) => ({
        ...prev,
        imagenes: 'Formato de imagen no permitido. Usa JPG, PNG o WEBP.',
      }));
      return;
    }

    const ficherosDemasiadoGrandes = ficheros.filter(
      (fichero) => fichero.size > MAX_TAMANO_IMAGEN_BYTES
    );
    if (ficherosDemasiadoGrandes.length > 0) {
      setErrors((prev) => ({
        ...prev,
        imagenes: `Cada imagen debe pesar como máximo ${tamanoMaximoEnMB()} MB.`,
      }));
      return;
    }

    const nuevasImagenes = ficheros.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setImagenes((prev) => [...prev, ...nuevasImagenes]);
    setErrors((prev) => ({ ...prev, imagenes: '' }));
  };

  /**
   * Quita una imagen de la lista y revoca su object URL de previsualización.
   */
  const quitarImagen = (indice) => {
    setImagenes((prev) => {
      const imagenAQuitar = prev[indice];
      if (imagenAQuitar) {
        URL.revokeObjectURL(imagenAQuitar.previewUrl);
      }
      return prev.filter((_, i) => i !== indice);
    });
  };

  /**
   * Valida todos los campos obligatorios del formulario.
   * @returns {boolean} Si el formulario es válido.
   */
  const validateForm = () => {
    const newErrors = {};

    // Los límites replican los del validador del backend
    // (frontend/public/api/src/Validation/RecetaValidator.php). Si divergen,
    // el usuario pasa esta validación y se come un rechazo del servidor sin
    // entender por qué.
    const nombreTrim = formData.nombre.trim();
    if (!nombreTrim) {
      newErrors.nombre = 'El nombre es obligatorio';
    } else if (nombreTrim.length < 3 || nombreTrim.length > 120) {
      newErrors.nombre = 'El nombre debe tener entre 3 y 120 caracteres';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Introduce un correo electrónico válido';
    }

    const nombreRecetaTrim = formData.nombre_receta.trim();
    if (!nombreRecetaTrim) {
      newErrors.nombre_receta = 'El nombre de la receta es obligatorio';
    } else if (
      nombreRecetaTrim.length < MIN_CARACTERES_NOMBRE_RECETA ||
      nombreRecetaTrim.length > MAX_CARACTERES_NOMBRE_RECETA
    ) {
      newErrors.nombre_receta = `El nombre de la receta debe tener entre ${MIN_CARACTERES_NOMBRE_RECETA} y ${MAX_CARACTERES_NOMBRE_RECETA} caracteres`;
    }

    if (!formData.receta.trim()) {
      newErrors.receta = 'La receta es obligatoria';
    } else if (formData.receta.trim().length < MIN_CARACTERES_RECETA) {
      newErrors.receta = `Cuéntanos un poco más: mínimo ${MIN_CARACTERES_RECETA} caracteres`;
    }

    if (imagenes.length < MIN_IMAGENES) {
      newErrors.imagenes = 'Añade al menos una foto de tu receta.';
    }

    if (!formData.consentimientoPublicacion) {
      newErrors.consentimientoPublicacion = 'Debes autorizar la publicación para poder enviar tu receta';
    }

    if (!formData.aceptaPrivacidad) {
      newErrors.aceptaPrivacidad = 'Debes aceptar la política de privacidad para poder enviar tu receta';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Envía el formulario: valida, llama al servicio de envío y gestiona los
   * estados de carga, éxito y error. En caso de error, conserva los datos
   * introducidos para permitir reintentar sin volver a rellenarlos.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorEnvio('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await submitRecipe({
        nombre: formData.nombre,
        email: formData.email,
        nombre_receta: formData.nombre_receta,
        receta: formData.receta,
        imagenes: imagenes.map((imagen) => imagen.file),
        consentimientoPublicacion: formData.consentimientoPublicacion,
        aceptaPrivacidad: formData.aceptaPrivacidad,
      });

      setIsSubmitted(true);
    } catch (error) {
      // Si el backend devolvió errores por campo, se pintan bajo cada campo
      // igual que los de la validación local. El backend usa snake_case y los
      // dos checkboxes viven en el estado con nombres distintos, de ahí el
      // mapeo explícito.
      if (error.camposConError) {
        const equivalencias = {
          consentimiento_publicacion: 'consentimientoPublicacion',
          acepta_privacidad: 'aceptaPrivacidad',
        };
        const erroresDelServidor = {};

        Object.entries(error.camposConError).forEach(([campo, mensajes]) => {
          const clave = equivalencias[campo] || campo;
          erroresDelServidor[clave] = Array.isArray(mensajes)
            ? mensajes.join(' ')
            : String(mensajes);
        });

        setErrors(erroresDelServidor);
      }

      setErrorEnvio(
        error.message || 'No se ha podido enviar la receta. Inténtalo de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bloque de confirmación tras un envío exitoso, con el mismo lenguaje
  // visual que el de ContactForm.
  if (isSubmitted) {
    return (
      <div
        className="flex flex-col items-center justify-center text-center min-h-[45vh] py-8"
        role="status"
        aria-live="polite"
      >
        {/* Marca de verificación en los colores de la casa, no el verde
            genérico: la confirmación es parte de la web, no un aviso del
            navegador. */}
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-intercasa-red/10 mb-6">
          <svg
            className="w-10 h-10 text-intercasa-red"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h3 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-3">
          ¡Gracias! Hemos recibido tu receta
        </h3>

        <p className="font-lato text-intercasa-dark/70 text-base md:text-lg max-w-sm">
          Nuestro equipo la revisará antes de publicarla.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Banner de error de envío */}
      {errorEnvio && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{errorEnvio}</p>
        </div>
      )}

      {/* Nombre */}
      <div>
        <label htmlFor="nombre" className="block text-sm font-semibold text-intercasa-dark mb-2">
          Nombre completo<span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          id="nombre"
          name="nombre"
          value={formData.nombre}
          onChange={handleInputChange}
          aria-required="true"
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-intercasa-red transition-colors duration-300 ${
            errors.nombre ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Tu nombre completo"
        />
        {errors.nombre && <p className="text-red-600 text-sm mt-1">{errors.nombre}</p>}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-semibold text-intercasa-dark mb-2">
          Correo electrónico<span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          aria-required="true"
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-intercasa-red transition-colors duration-300 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="tu@correo.com"
        />
        {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
      </div>

      {/* Separador visual sutil: a partir de aquí, los campos propios de la
          receta (nombre, ingredientes/elaboración y fotos), diferenciados de
          los datos de contacto anteriores. */}
      <div className="border-t border-gray-200 pt-6 space-y-6">
        <p className="font-lato text-xs font-semibold text-gray-400 uppercase tracking-wide">
          Tu receta
        </p>

        {/* Nombre de la receta */}
        <div>
          <label htmlFor="nombre_receta" className="block text-sm font-semibold text-intercasa-dark mb-2">
            Nombre de la receta<span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
          </label>
          <input
            type="text"
            id="nombre_receta"
            name="nombre_receta"
            value={formData.nombre_receta}
            onChange={handleInputChange}
            aria-required="true"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-intercasa-red transition-colors duration-300 ${
              errors.nombre_receta ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Papas arrugadas con mojo de cilantro"
          />
          {errors.nombre_receta && <p className="text-red-600 text-sm mt-1">{errors.nombre_receta}</p>}
        </div>

        {/* Ingredientes y elaboración (clave interna 'receta': el backend ya
            espera ese nombre de campo). */}
        <div>
          <label htmlFor="receta" className="block text-sm font-semibold text-intercasa-dark mb-2">
            Ingredientes y elaboración<span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
          </label>
          <textarea
            id="receta"
            name="receta"
            value={formData.receta}
            onChange={handleInputChange}
            rows={8}
            aria-required="true"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-intercasa-red transition-colors duration-300 resize-vertical ${
              errors.receta ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enumera primero los ingredientes y después los pasos de elaboración de tu receta con productos Intercasa..."
          />
          {errors.receta && (
            <p className="text-red-600 text-sm mt-1">{errors.receta}</p>
          )}
        </div>

        {/* Imágenes */}
        <div>
          <label className="block text-sm font-semibold text-intercasa-dark mb-2">
            Fotos de tu receta<span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
          </label>
          {/*
            Se mantiene el peso máximo a la vista, aunque alargue el texto: una
            foto de móvil actual supera con facilidad los 5 MB, y avisar antes
            evita que el usuario elija la imagen y se coma un error después.
          */}
          <p className="text-sm text-gray-500 mb-2">
            De 1 a {MAX_IMAGENES} fotos · JPG, PNG o WEBP · máx. {tamanoMaximoEnMB()} MB cada una
          </p>

          {imagenes.length < MAX_IMAGENES && (
            <label
              htmlFor="imagenes"
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-6 px-4 cursor-pointer hover:border-intercasa-red transition-colors duration-300 text-center"
            >
              <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-500">
                Haz clic para subir imágenes (JPG, PNG o WEBP, máx. {tamanoMaximoEnMB()} MB)
              </span>
              <input
                type="file"
                id="imagenes"
                name="imagenes"
                accept="image/jpeg,image/png,image/webp"
                multiple
                aria-required="true"
                onChange={handleImagenesChange}
                className="hidden"
              />
            </label>
          )}

          {errors.imagenes && <p className="text-red-600 text-sm mt-1">{errors.imagenes}</p>}

          {imagenes.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {imagenes.map((imagen, indice) => (
                <div key={imagen.previewUrl} className="relative w-20 h-20 rounded-lg overflow-hidden group">
                  <img
                    src={imagen.previewUrl}
                    alt={`Previsualización ${indice + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => quitarImagen(indice)}
                    aria-label={`Quitar imagen ${indice + 1}`}
                    className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-intercasa-dark/80 text-white rounded-full text-xs hover:bg-intercasa-red transition-colors duration-300"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/*
        Dos checkboxes separados y no uno solo: el RGPD exige consentimiento
        diferenciado por finalidad. Publicar la receta y las imágenes en la
        web y redes sociales es una finalidad; tratar los datos de contacto
        para gestionar el envío de la receta es otra finalidad distinta.
        Fusionar ambos consentimientos en un único checkbox invalidaría el
        consentimiento, porque el usuario no podría aceptar uno sin el otro.
      */}
      <div className="space-y-4">
        {/* Consentimiento de publicación */}
        <div>
          <label htmlFor="consentimientoPublicacion" className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              id="consentimientoPublicacion"
              name="consentimientoPublicacion"
              checked={formData.consentimientoPublicacion}
              onChange={handleInputChange}
              aria-required="true"
              className="mt-1 w-4 h-4 accent-intercasa-red flex-shrink-0"
            />
            <span className="text-sm text-intercasa-dark">
              {TEXTO_CONSENTIMIENTO}
              <span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
            </span>
          </label>
          {errors.consentimientoPublicacion && (
            <p className="text-red-600 text-sm mt-1">{errors.consentimientoPublicacion}</p>
          )}
        </div>

        {/* Consentimiento de política de privacidad (RGPD) */}
        <div>
          <label htmlFor="aceptaPrivacidad" className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              id="aceptaPrivacidad"
              name="aceptaPrivacidad"
              checked={formData.aceptaPrivacidad}
              onChange={handleInputChange}
              aria-required="true"
              className="mt-1 w-4 h-4 accent-intercasa-red flex-shrink-0"
            />
            <span className="text-sm text-intercasa-dark">
              He leído y acepto la{' '}
              {/* El ancla #recetas abre el apartado de recetas de la política */}
              <Link to="/privacidad#recetas" className="underline">
                política de privacidad
              </Link>{' '}
              y el tratamiento de mis datos para gestionar el envío de mi receta.
              <span className="text-intercasa-red ml-1" aria-hidden="true">*</span>
            </span>
          </label>
          {errors.aceptaPrivacidad && (
            <p className="text-red-600 text-sm mt-1">{errors.aceptaPrivacidad}</p>
          )}
        </div>
      </div>

      {/* Botón de envío */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full inline-flex items-center justify-center bg-intercasa-red text-white font-semibold px-8 py-4 rounded-lg hover:bg-intercasa-dark transition-colors duration-300 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? 'Enviando…' : 'Enviar mi receta'}
        </button>
      </div>
    </form>
  );
};

RecipeSubmissionForm.propTypes = {
  onEnviado: PropTypes.func.isRequired,
  onEstadoEnvio: PropTypes.func,
};

RecipeSubmissionForm.defaultProps = {
  onEstadoEnvio: undefined,
};

export default RecipeSubmissionForm;
